import { Dvr, DvrQuota, Profile } from 'models'
import { has } from 'lodash'
import Constants from 'api-constants'
import { getNagraDeviceType } from '../helpers'
import { RATING_MAPPING } from '../config'

import { addImageResizingAttributes } from '../helpers'

const DEFAULT_LANGUAGE = 'en'

/**
 * On starhub, the channel platform id is always the Android ONE including for IOS device due to mistake done initially at project start where platformChannelId
 * It is OK for feature like favorites to use this ID specifically, we have to choice to maintain to use this ID otherwise the existing users will lose their Favorites/Information
 * @todo To move in configuration parameter in the future if needed
 */
const MIGRATION_STARHUB = true

const PVRStatusMapping = {
    SCHEDULED: Constants.recordingStatus.scheduled,
    RECORDED: Constants.recordingStatus.completed,
    RECORDING: Constants.recordingStatus.ongoing,
    FAILED: Constants.recordingStatus.failed,
}

export const dvrFactory = (
    dvr = {},
    platform = Constants.platform.android,
    clientConfig = {},
    language = DEFAULT_LANGUAGE
) => {
    if (!dvr) return

    let metadata = {}
    if (has(dvr, 'metadata')) {
        if (has(dvr.metadata, language)) {
            metadata = dvr.metadata[language]
        } else {
            if (language.length >= 2) {
                language = language.substr(0, 2)
            }
            let localeKey = Object.keys(dvr.metadata).find((x) => x && x.startsWith(language))
            if (!localeKey) {
                localeKey = Object.keys(dvr.metadata).find(
                    (x) => x && x.startsWith(DEFAULT_LANGUAGE)
                )
            }
            if (localeKey) {
                metadata = dvr.metadata[localeKey]
            }
        }
    }

    const picture =
        Object.values(dvr.images)[0] || metadata.PromoImages
            ? metadata.PromoImages[0]
            : null || null

    let playbackId
    let technicalId
    let playbackUrl
    const dvrStreams = dvr.playback[getNagraDeviceType(platform)]
    if (dvrStreams) {
        playbackId = dvrStreams.drmId
        technicalId = dvrStreams.technicalId
        playbackUrl = dvrStreams.uri
    }

    if (MIGRATION_STARHUB) {
        /**
         * On starhub, the channel platform id is always the Android ONE including for IOS device due to mistake done initially at project start where platformChannelId
         * It is OK for feature like favorites to use this ID specifically, we have to choice to maintain to use this ID otherwise the existing users will lose their Favorites/Information
         * @todo To move in configuration parameter in the future if needed
         */
        const androidStream = dvr.playback[getNagraDeviceType(Constants.platform.android)]
        if (androidStream) {
            technicalId = dvrStreams.technicalId
        }
    }

    let chromecastPlaybackUrl
    let chromecastPlaybackId
    if (platform === Constants.platform.ios) {
        const chromecastStreams = dvr.playback[getNagraDeviceType(Constants.platform.android)]
        if (chromecastStreams) {
            chromecastPlaybackId = chromecastStreams.drmId
            chromecastPlaybackUrl = chromecastStreams.uri
        }
    }

    const ratingCode = has(metadata, 'ratings') ? metadata?.ratings?.code : undefined

    /**
     * /!\ NOTE: This rating table is specific to starhub. In case of new project involving Nagra backend
     * for another customer, inject this configuration outside the nagra service
     */
    const ratingId = RATING_MAPPING[ratingCode]
    const pcValue = (clientConfig.pcLevels || {})[ratingId]

    return new Dvr({
        id: dvr.id,
        recordingId: dvr.id,
        eventIds: [dvr.eventId],
        titleId: dvr.eventId,
        platformChannelId: dvr.channelId || technicalId,
        startTime: new Date(dvr.start).valueOf(),
        endTime: new Date(dvr.end).valueOf(),
        status: PVRStatusMapping[dvr.status] || '',
        duration: dvr.duration,
        title: metadata.title,
        description: has(metadata, ['Description']) ? metadata.Description : undefined,
        episodeNumber: metadata.episodeNumber,
        seasonNumber: metadata.SeasonNumber,
        genres: Array.isArray(metadata.Genre) ? metadata.Genre : [],
        ratingId,
        pcValue,
        thumbnail: addImageResizingAttributes(picture, 'thumbnail'),
        backdrop: addImageResizingAttributes(picture, 'backdrop'),
        playbackId,
        playbackUrl,
        isProtected: dvr.isProtected,
        chromecastPlaybackId,
        chromecastPlaybackUrl,
        contentType: dvr.contentType,
        // TODO for now only Non Serie recording. To be integrated later
        // isDvrEpisode: !!metadata.episodeNumber,
        // seriesId: dvr.seriesId,
        // seriesName: TODO
        // episodeTitle: TODO
    })
}

export const dvrQuotaFactory = (quota = {}) => {
    const { currentUsage: used = 0, quotaTotal: total = 0 } = quota

    const dvrQuota = Number(total) / 3600
    const dvrQuotaRemaining = dvrQuota - Number(used) / 3600

    return new DvrQuota({
        unit: Profile.DVR_QUOTA_UNIT.HOUR,
        dvrQuota,
        dvrQuotaRemaining: dvrQuotaRemaining,
    })
}
